/**
 * FoundersDealBanner component for showing founder's deal with scarcity.
 * 
 * This component replaces the coupon status banner with a more professional
 * urgency-driven message that doesn't expose coupon codes.
 */

import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  LocalOffer as DealIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import api from '../utils/api';
import analytics from '../services/analytics';
import { useAuth } from '../context/AuthContext';

const FoundersDealBanner = ({ showOnlyIfAvailable = false }) => {
  const [couponData, setCouponData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadCouponStatus();
  }, []);

  const loadCouponStatus = async () => {
    try {
      const response = await api.get('/coupon/all-status');
      setCouponData(response.data);
      
      // Track when founder's deal is viewed
      if (response.data?.coupons?.length > 0 && !hasTrackedView) {
        const foundersDeal = response.data.coupons.find(c => c.coupon_code === 'ALPHA90');
        if (foundersDeal && foundersDeal.is_available) {
          analytics.track('founders_deal_viewed', {
            remaining_uses: foundersDeal.remaining_uses,
            max_uses: foundersDeal.max_uses,
            user_has_used: foundersDeal.user_has_used,
            is_alpha_tester: user?.is_alpha_tester || false
          });
          setHasTrackedView(true);
        }
      }
    } catch (err) {
      console.error('Failed to load deal status:', err);
      setError('Failed to load deal availability');
    } finally {
      setLoading(false);
    }
  };

  if (loading || error) {
    return null;
  }

  if (!couponData || couponData.coupons.length === 0) {
    return null;
  }

  // Find the founder's deal coupon (90% discount)
  const foundersDeal = couponData.coupons.find(c => c.coupon_code === 'ALPHA90');
  
  if (!foundersDeal) {
    return null;
  }

  // If showing only when available and it's not available, hide
  if (showOnlyIfAvailable && !foundersDeal.is_available) {
    return null;
  }

  const getSeverity = () => {
    if (!foundersDeal.is_available) return 'error';
    if (foundersDeal.remaining_uses <= 5) return 'warning';
    return 'info';
  };

  const getMessage = () => {
    if (!foundersDeal.is_available) {
      return 'Founder\'s Deal has ended - all spots taken!';
    }
    
    const total = foundersDeal.max_uses;
    const remaining = foundersDeal.remaining_uses;
    
    // Show strikethrough only if some spots have been taken
    const spotsUsed = total - remaining;
    return (
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" variant="body2">
          Founder's Deal - 90% OFF for 3 months!
        </Typography>
        <Typography component="span" variant="body2" sx={{ fontWeight: 'bold' }}>
          {spotsUsed > 0 && (
            <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{total}</span>
          )}{' '}
          {remaining} {remaining === 1 ? 'spot' : 'spots'} left!
        </Typography>
      </Box>
    );
  };

  const getIcon = () => {
    if (foundersDeal.remaining_uses <= 5) {
      return <TimerIcon />;
    }
    return <DealIcon />;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Alert 
        severity={getSeverity()}
        icon={getIcon()}
        sx={{
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          {getMessage()}
          {foundersDeal.user_has_used && (
            <Chip 
              label="You're a Founder!" 
              size="small" 
              color="success"
              variant="outlined"
            />
          )}
        </Box>
        
        {!foundersDeal.user_has_used && foundersDeal.is_available && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Limited time offer for early supporters who help shape our platform
          </Typography>
        )}
      </Alert>
    </Box>
  );
};

export default FoundersDealBanner;